import { deliveryStatusLabel, receiptOutcomeLabel } from './findings';
import type { DeliveryInfo, ReceiptInfo, ReviewDocument, Revision } from './schema';
import styles from './review.module.css';

export interface DeliverySummaryProps {
  readonly readyRevision: Revision;
  readonly readyDocument: ReviewDocument;
  readonly delivery: DeliveryInfo | null;
  readonly receipt: ReceiptInfo | null;
}

/**
 * What was actually approved for Excel, and what happened to it - kept visually and textually
 * separate from the editable draft above it (`docs/implementation/06-mobile-web.md` 6.6: "show
 * immutable delivered revision and receipt summary"; `docs/architecture.md` §4: `ready_for_excel`
 * "is immutable for this delivery"). Every field here comes from `NoteDetailResponse.ready_*`,
 * which the server reads from the frozen revision - never from `document`, which follows the
 * *current* revision and is free to move on after a correction.
 *
 * Deliberately says only what is proven: `delivery`/`receipt` may both still be `null` right
 * after approval (the agent has not leased the work yet), and this component never implies
 * Excel already has the sale until a `receipt` exists to say so.
 */
export function DeliverySummary({
  readyRevision,
  readyDocument,
  delivery,
  receipt,
}: DeliverySummaryProps) {
  return (
    <section className={styles.section} aria-labelledby="delivery-summary-heading">
      <h2 id="delivery-summary-heading" className={styles.sectionTitle}>
        Lo que se aprobó para Excel
      </h2>
      <p className={styles.deliveryMeta}>
        Versión #{readyRevision.revision_no}
        {readyRevision.reviewed_at !== null
          ? ` · aprobada el ${readyRevision.reviewed_at.slice(0, 10)}`
          : ''}
        . Esta versión no cambia aunque la nota se corrija después.
      </p>

      <div className={styles.deliveryDocument}>
        <p>
          <strong>Cliente:</strong> {readyDocument.customer.official_name ?? '(sin resolver)'}
        </p>
        {readyDocument.note_date !== null ? (
          <p>
            <strong>Fecha:</strong> {readyDocument.note_date}
          </p>
        ) : null}
        <ul className={styles.deliveryLines}>
          {readyDocument.lines.map((line) => (
            <li key={line.line_key}>
              {line.product.official_name ?? line.product.recognized_text ?? '(sin concepto)'} —{' '}
              {line.quantity ?? '?'} × {line.unit_price ?? '?'} = {line.line_amount ?? '?'}
            </li>
          ))}
        </ul>
        <p>
          <strong>Total escrito:</strong> {readyDocument.written_total ?? '(sin total)'}
        </p>
      </div>

      {delivery !== null ? (
        <p className={styles.deliveryMeta}>
          Estado del envío: <strong>{deliveryStatusLabel(delivery.status)}</strong>
        </p>
      ) : null}

      {receipt !== null ? (
        <div
          className={
            receipt.outcome === 'verified'
              ? styles.deliveryReceiptVerified
              : styles.deliveryReceiptAmbiguous
          }
        >
          <p>
            <strong>{receiptOutcomeLabel(receipt.outcome)}</strong>
          </p>
          {receipt.outcome === 'verified' ? (
            <p>
              {receipt.inserted_row_count === 1
                ? '1 fila verificada en Excel.'
                : `${String(receipt.inserted_row_count)} filas verificadas en Excel.`}
            </p>
          ) : null}
          <p className={styles.deliveryMeta}>
            Confirmado por el agente el {receipt.acknowledged_at.slice(0, 10)}
          </p>
        </div>
      ) : null}
    </section>
  );
}
